import {
    Avatar,
    Box,
    Button,
    ButtonBase,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Menu,
    MenuItem,
    Switch,
    Tooltip,
    Typography,
} from "@mui/material";
import LightIcon from "@mui/icons-material/LightMode";
import DarkIcon from "@mui/icons-material/DarkMode";
import { observer } from "mobx-react-lite";
import AuthStore from "../stores/AuthStore";
import DialogStore from "../stores/DialogStore";
import ThemeStore from "../stores/ThemeStore";
import React from "react";
import SquareDeviceEndpoints from "../api/SquareDeviceEndpoints";
import {
    SimpleSquareDevice,
    SquareDeviceStatus,
    squareDeviceStatusFriendlyName,
} from "../models/Event";
import SquareDeviceStore from "../stores/SquareDeviceStore";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import Loading from "./common/Loading";
import NothingHereYet from "./NothingHereYet";
import InputField from "./form/InputField";
import Countdown from "./common/Countdown";
import { DateTime } from "luxon";
import { useCartStore } from "../stores/CartStore";
import toast from "../utils/toast";

type Props = {
    clientId: number;
};

const SessionWidget = observer(({ clientId }: Props) => {
    const [devices, setDevices] = React.useState<SimpleSquareDevice[]>([]);
    const [squareOpen, setSquareOpen] = React.useState(false);
    const [squareRequest, setSquareRequest] = React.useState({
        name: "",
    });
    const [addMode, setAddMode] = React.useState(false);
    const [newTerminal, setNewTerminal] =
        React.useState<SimpleSquareDevice | null>(null);
    const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
    const [terminalLoading, setTerminalLoading] = React.useState(false);

    const cartStore = useCartStore();

    function onClose() {
        setSquareOpen(false);
        setAddMode(false);
        setSquareRequest({
            name: "",
        });
    }

    function handleClick(e: React.MouseEvent<HTMLElement>) {
        setAnchorEl(e.currentTarget);
    }

    function handleClose() {
        setAnchorEl(null);
    }

    React.useEffect(() => {
        SquareDeviceStore.getSquareDevicesForClient(clientId);
    }, []);

    return (
        <>
            {AuthStore.loggedIn && (
                <>
                    <Box
                        sx={{
                            ml: {
                                lg: 2,
                            },
                            display: "flex",
                            justifyContent: {
                                xs: "center",
                                lg: "flex-start",
                            },
                        }}
                    >
                        <Tooltip title="Options">
                            <ButtonBase onClick={handleClick}>
                                <Card>
                                    <CardContent
                                        sx={{
                                            padding: "20px",
                                            display: "flex",
                                            gap: "10px",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Avatar />
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "flex-start",
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                                fontWeight={600}
                                            >
                                                {AuthStore.fullname}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                fontWeight={600}
                                                color={
                                                    AuthStore.boxoffice
                                                        ? "info.main"
                                                        : "success.main"
                                                }
                                            >
                                                {AuthStore.boxoffice
                                                    ? "Box-Office Mode"
                                                    : "Ecommerce Mode"}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </ButtonBase>
                        </Tooltip>
                        <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl)}
                            onClose={handleClose}
                        >
                            <MenuItem
                                onClick={() => {
                                    DialogStore.showAlert(
                                        "Toggle Box-Office Mode",
                                        "Are you sure you want to toggle box-office? This will clear your cart.",
                                        () => AuthStore.toggleBoxOffice(),
                                    );
                                    handleClose();
                                }}
                            >
                                Switch to{" "}
                                {AuthStore.boxoffice
                                    ? "Ecommerce"
                                    : "Box-Office"}{" "}
                                Mode
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    DialogStore.showAlert(
                                        "Reset Cart",
                                        "Are you sure you want to reset your cart?",
                                        () => cartStore?.nextCart(true),
                                    );
                                }}
                            >
                                Reset Cart
                            </MenuItem>
                            <MenuItem onClick={() => ThemeStore.toggleMode()}>
                                Switch to{" "}
                                {ThemeStore.mode === "dark" ? "Light" : "Dark"}{" "}
                                Mode
                            </MenuItem>
                            {AuthStore.boxoffice && (
                                <React.Fragment>
                                    <Divider />
                                    <MenuItem
                                        onClick={() => {
                                            setSquareOpen(true);
                                            handleClose();
                                        }}
                                    >
                                        Manage Square Terminals
                                    </MenuItem>
                                </React.Fragment>
                            )}
                        </Menu>
                        {/* <IconButton onClick={() => ThemeStore.toggleMode()}>
                            {ThemeStore.mode === "dark" ? (
                                <LightIcon />
                            ) : (
                                <DarkIcon />
                            )}
                        </IconButton> */}
                        {AuthStore.boxoffice && (
                            <Box>
                                {/* <Button
                                    variant="contained"
                                    color="info"
                                    size="small"
                                    onClick={() => setSquareOpen(true)}
                                >
                                    Square Terminals
                                </Button> */}
                                <Dialog
                                    open={squareOpen}
                                    fullWidth
                                    maxWidth="md"
                                >
                                    <DialogTitle
                                        sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                        }}
                                    >
                                        <Typography variant="h5">
                                            {addMode
                                                ? "Add Square Terminal"
                                                : "Square Terminals"}
                                        </Typography>
                                        <IconButton onClick={onClose}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </DialogTitle>
                                    <DialogContent>
                                        {!addMode && (
                                            <Box>
                                                <List>
                                                    {SquareDeviceStore.devices
                                                        // .filter(
                                                        //     (d) =>
                                                        //         d.isActive ||
                                                        //         DateTime.fromISO(
                                                        //             d.pairByDate,
                                                        //         ) >
                                                        //             DateTime.now(),
                                                        // )
                                                        .map((device) => (
                                                            <ListItem
                                                                key={device.id}
                                                                disabled={
                                                                    !device.isActive
                                                                }
                                                                // selected={
                                                                //     SquareDeviceStore.selectedDeviceId ===
                                                                //     device.id
                                                                // }
                                                                // onClick={() => {
                                                                //     SquareDeviceStore.selectDevice(
                                                                //         device.id,
                                                                //     );

                                                                //     setSquareOpen(
                                                                //         false,
                                                                //     );
                                                                // }}
                                                            >
                                                                <ListItemText
                                                                    primary={
                                                                        device.deviceName
                                                                    }
                                                                />
                                                                <Chip
                                                                    label={squareDeviceStatusFriendlyName(
                                                                        device.status,
                                                                    )}
                                                                    size="small"
                                                                    color={
                                                                        device.isActive
                                                                            ? "success"
                                                                            : "default"
                                                                    }
                                                                />
                                                                <IconButton
                                                                    onClick={() => {
                                                                        DialogStore.showAlert(
                                                                            "Remove Terminal",
                                                                            "Are you sure you want to remove this terminal?",
                                                                            async () => {
                                                                                await SquareDeviceEndpoints.deleteSquareDeviceForClient(
                                                                                    clientId,
                                                                                    device.id,
                                                                                );
                                                                                SquareDeviceStore.getSquareDevicesForClient(
                                                                                    clientId,
                                                                                );
                                                                            },
                                                                        );
                                                                    }}
                                                                >
                                                                    <CloseIcon />
                                                                </IconButton>
                                                            </ListItem>
                                                        ))}
                                                    {SquareDeviceStore.loading && (
                                                        <Loading />
                                                    )}
                                                    {SquareDeviceStore.devices
                                                        .length === 0 && (
                                                        <NothingHereYet message="No terminals found." />
                                                    )}
                                                </List>
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        justifyContent:
                                                            "flex-end",
                                                    }}
                                                >
                                                    <Button
                                                        variant="contained"
                                                        color="success"
                                                        onClick={() =>
                                                            setAddMode(true)
                                                        }
                                                    >
                                                        Add New Terminal
                                                    </Button>
                                                </Box>
                                            </Box>
                                        )}
                                        {addMode && !newTerminal && (
                                            <Box
                                                component="form"
                                                onSubmit={async (e) => {
                                                    e.preventDefault();

                                                    if (!squareRequest.name) {
                                                        toast.error(
                                                            "Please enter a terminal name.",
                                                        );
                                                        return;
                                                    }

                                                    setTerminalLoading(true);

                                                    try {
                                                        const t =
                                                            await SquareDeviceEndpoints.createSquareDeviceForClient(
                                                                clientId,
                                                                squareRequest.name,
                                                            );
                                                        setSquareRequest({
                                                            name: "",
                                                        });
                                                        setNewTerminal(t);
                                                    } catch (err) {
                                                    } finally {
                                                        setTerminalLoading(
                                                            false,
                                                        );
                                                    }
                                                }}
                                            >
                                                <Grid container spacing={2}>
                                                    <Grid item xs={12}>
                                                        <InputField
                                                            label="Terminal Name"
                                                            fullWidth
                                                            value={
                                                                squareRequest.name
                                                            }
                                                            onChange={(e) =>
                                                                setSquareRequest(
                                                                    {
                                                                        ...squareRequest,
                                                                        name: e
                                                                            .target
                                                                            .value,
                                                                    },
                                                                )
                                                            }
                                                        />
                                                    </Grid>
                                                    <Grid
                                                        item
                                                        xs={12}
                                                        sx={{
                                                            display: "flex",
                                                            justifyContent:
                                                                "space-between",
                                                        }}
                                                    >
                                                        <Button
                                                            variant="contained"
                                                            color="secondary"
                                                            onClick={() =>
                                                                setAddMode(
                                                                    false,
                                                                )
                                                            }
                                                        >
                                                            Back
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            color="success"
                                                            type="submit"
                                                            disabled={
                                                                terminalLoading
                                                            }
                                                        >
                                                            Add Terminal
                                                        </Button>
                                                    </Grid>
                                                </Grid>
                                            </Box>
                                        )}
                                        {addMode && newTerminal && (
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    gap: "10px",
                                                }}
                                            >
                                                <Typography variant="h4">
                                                    {newTerminal.deviceName}
                                                </Typography>
                                                <Typography
                                                    variant="h5"
                                                    sx={{ textAlign: "center" }}
                                                >
                                                    Use the following device
                                                    code to sign in on the
                                                    terminal, then click{" "}
                                                    <em>Check Status</em>.
                                                </Typography>
                                                <Card>
                                                    <CardContent sx={{ px: 4 }}>
                                                        <Typography
                                                            variant="h5"
                                                            color="textSecondary"
                                                        >
                                                            {
                                                                newTerminal.deviceCode
                                                            }
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                                <Typography>
                                                    Code expres in:{" "}
                                                    <Countdown
                                                        time={DateTime.fromISO(
                                                            newTerminal.dateCreated,
                                                        ).toMillis()}
                                                        ttl={300000}
                                                        onTimeUp={() => {
                                                            setNewTerminal(
                                                                null,
                                                            );
                                                            setAddMode(false);
                                                            setSquareRequest({
                                                                name: "",
                                                            });
                                                            SquareDeviceStore.getSquareDevicesForClient(
                                                                clientId,
                                                            );
                                                        }}
                                                    />
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    color="info"
                                                    onClick={async () => {
                                                        const d =
                                                            await SquareDeviceEndpoints.getSquareDeviceById(
                                                                clientId,
                                                                newTerminal.id,
                                                            );

                                                        setNewTerminal(d);

                                                        if (
                                                            d.status ===
                                                            SquareDeviceStatus.PAIRED
                                                        ) {
                                                            SquareDeviceStore.getSquareDevicesForClient(
                                                                clientId,
                                                            );
                                                            setAddMode(false);
                                                            setSquareRequest({
                                                                name: "",
                                                            });
                                                            setNewTerminal(
                                                                null,
                                                            );
                                                        }
                                                    }}
                                                >
                                                    Check Status
                                                </Button>
                                            </Box>
                                        )}
                                    </DialogContent>
                                </Dialog>
                            </Box>
                        )}
                    </Box>
                </>
            )}
        </>
    );
});

export default SessionWidget;
